/**
 * (c) 2021-2022, Nous Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useIsUnmounted from "../common/use-is-unmounted";
import { apiDocs, apiDocsRequestType, ApiDocsResponse } from "../language-server/apidocs";
import { extensionsDocs, extensionsDocsRequestType, ExtensionsDocsResponse } from "../language-server/extensionsdocs";
import { useLanguageServerClient } from "../language-server/language-server-hooks";
import { useLogging } from "../logging/logging-hooks";
import { useSettings } from "../settings/settings";
import dragImage from "./drag-image.svg";
import { fetchIdeas } from "./ideas/content";
import { Idea } from "./ideas/model";
import { ApiReferenceMap, fetchMappingData } from "./mapping/content";
// import { ExtensionModule } from "./mapping/model";
import { fetchReferenceToolkit } from "./reference/content";
import { Toolkit } from "./reference/model";
import { useSelector } from 'react-redux'
import { RootState } from '../store/modalStore'

export type ContentState<T> =
  | { status: "ok"; content: T }
  | { status: "error" }
  | { status: "loading" };

const useContent = <T,>(
  fetchContent: (languageId: string) => Promise<T>
): ContentState<T> => {
  const [state, setState] = useState<ContentState<T>>({
    status: "loading",
  });
  const logging = useLogging();
  const isUnmounted = useIsUnmounted();
  const [{ languageId }] = useSettings();
  useEffect(() => {
    const load = async () => {
      try {
        const content = await fetchContent(languageId);
        if (!isUnmounted()) {
          setState({ status: "ok", content });
        }
      } catch (e) {
        logging.error(e);
        if (!isUnmounted()) {
          setState({
            status: "error",
          });
        }
      }
    };
    load();
  }, [setState, isUnmounted, logging, languageId, fetchContent]);
  return state;
};
// api
export const useApiDocumentation = (): ApiDocsResponse | undefined => {
  const client = useLanguageServerClient();
  const [apidocs, setApiDocs] = useState<ApiDocsResponse | undefined>();
  const modulesList = useSelector((state: RootState) => state.apiStore.modulesList);
  const modulesType = useSelector((state: RootState) => state.apiStore.modulesType);
  // if (apidocs) {
  //   console.log('modulesList', modulesList, modulesList.length, Object.keys(apidocs).length);
  // }
  
  if (modulesType === 'add' && apidocs && modulesList.length !== Object.keys(apidocs).length) {
    const fetchData = async () => {
      try {
        if (client) {
          await client.initialize();
          const docs = await apiDocs(client, modulesList);
          setApiDocs(docs);
        }
      } catch (error) {
      }
    };
    fetchData()
  }
  if (modulesType === 'delete' && apidocs && modulesList.length !== Object.keys(apidocs).length) {
    const fetchData = async () => {
      try {
        if (client) {
          await client.initialize();
          const docs = await apiDocs(client, modulesList);
          setApiDocs(docs);
        }
      } catch (error) {
      }
    };
    fetchData()
  }

  useEffect(() => {
    const load = async () => {
      if (client) {
        await client.initialize();
        const docs = await apiDocs(client, null);
        setApiDocs(docs);
      }
    };
    load();
  }, [client]);
  return apidocs;
};

// 扩展中心
export const useExtensionsDocumentation = (): ExtensionsDocsResponse | undefined => {
  const client = useLanguageServerClient();
  const [extensionsdocs, setExtensionsDocs] = useState<ExtensionsDocsResponse | undefined>();
  // const modulesList = useSelector((state: RootState) => state.apiStore.modulesList);
  // const modulesType = useSelector((state: RootState) => state.apiStore.modulesType);

  useEffect(() => {
    const load = async () => {
      if (client) {
        await client.initialize();
        const docs = await extensionsDocs(client, null);
        setExtensionsDocs(docs);
      }
    };
    load();
  }, [client]);
  return extensionsdocs;
};

export interface DocumentationContextValue {
  api: ApiDocsResponse | undefined;
  ideas: ContentState<Idea[]>;
  reference: ContentState<Toolkit>;
  apiReferenceMap: ContentState<ApiReferenceMap>;
  // extensions: ExtensionModule[];
  extensions: ExtensionsDocsResponse  | undefined;
}

const DocumentationContext = createContext<
  DocumentationContextValue | undefined
>(undefined);

/**
 * Aggregated documentation.
 */
export const useDocumentation = (): DocumentationContextValue => {
  const value = useContext(DocumentationContext);
  if (!value) {
    throw new Error("Missing provider!");
  }
  return value;
};

const DocumentationProvider = ({ children }: { children: ReactNode }) => {
  const api = useApiDocumentation();
  const extensions = useExtensionsDocumentation();
  const ideas = useContent(fetchIdeas);
  const reference = useContent(fetchReferenceToolkit);
  const apiReferenceMap = useContent(fetchMappingData);
  const intl = useIntl();
  // const extensions = [
  //   // {
  //   //   src: "https://coding.matatalab.com/static/assets/8995cf9ee1b283825863a0c1b0485d1a.png",
  //   //   name: intl.formatMessage({ id: "gui.extension.matatacar.ircMode" }),
  //   //   desc: intl.formatMessage({ id: "gui.extension.matatacar.ircRemote" }),
  //   //   extensionId: 'irc',
  //   //   disabled: 'false'
  //   // },
  //   // {
  //   //   src: "https://coding.matatalab.com/static/assets/6fe9fef31774f574f16ded31d44dd656.png",
  //   //   name: intl.formatMessage({ id: "gui.extension.matatacar.legoMotorName" }),
  //   //   desc: intl.formatMessage({ id: "gui.extension.matatacar.legoMotorDesc" }),
  //   //   extensionId: 'legoMotor',
  //   //   disabled: 'false'
  //   // },
  //   // {
  //   //   src: "https://coding.matatalab.com/static/assets/66f66deb2e5daf84343a9e4faac6d74d.png",
  //   //   name: intl.formatMessage({ id: "gui.extension.matatacar.dcMotorName" }),
  //   //   desc: intl.formatMessage({ id: "gui.extension.matatacar.dcMotorDesc" }),
  //   // },
  //   // {
  //   //   src: "https://coding.matatalab.com/static/assets/18c4644d34396f7e7b776eb08cf8a93b.png",
  //   //   name: intl.formatMessage({ id: "gui.extension.matatacar.servoName" }),
  //   //   desc: intl.formatMessage({ id: "gui.extension.matatacar.servoDesc" }),
  //   // },
  //   {
  //     src: "https://coding.matatalab.com/static/assets/4e5be99dbdb1ef786227243ed514eacf.png",
  //     name: intl.formatMessage({ id: "gui.extension.matatacar.tinymlName", defaultMessage: 'TinyML' }),
  //     desc: intl.formatMessage({ id: "gui.extension.matatacar.tinymlDesc", defaultMessage: 'Creating new models for embedded machine learning' }),
  //     extensionId: 'techable_machine',
  //   },
  //   {
  //     src: "https://coding.matatalab.com/static/assets/68e366e6cb3f8eae4f3cf134ba69db2a.png",
  //     name: intl.formatMessage({ id: "gui.extension.matatacar.iotName", defaultMessage: 'IoT' }),
  //     desc: intl.formatMessage({ id: "gui.extension.matatacar.iotDesc", defaultMessage: 'Internet of things' }),
  //     extensionId: 'loT',
  //     // children: 
  //   },
  // ];
  const value: DocumentationContextValue = useMemo(() => {
    return { reference, api, ideas, apiReferenceMap, extensions };
  }, [reference, api, ideas, apiReferenceMap, extensions]);
  return (
    <DocumentationContext.Provider value={value}>
      {children}
    </DocumentationContext.Provider>
  );
};

export default DocumentationProvider;

let dragImageRefCount = 0;

export const useCodeDragImage = (): RefObject<HTMLImageElement | undefined> => {
  const ref = useRef<HTMLImageElement>();
  useEffect(() => {
    const id = "code-drag-image";
    let img = document.getElementById(id) as HTMLImageElement | null;
    if (!img) {
      img = new Image();
      img.id = id;
      img.alt = "";
      img.src = dragImage;
      img.style.position = "absolute";
      img.style.top = "0";
      img.style.zIndex = "-1";
      // Seems to need to be in the DOM for Safari.
      document.body.appendChild(img);
    }
    ref.current = img;
    dragImageRefCount++;
    return () => {
      if (!img) {
        throw new Error();
      }
      dragImageRefCount--;
      if (dragImageRefCount === 0) {
        img.remove();
      }
    };
  }, []);
  return ref;
};
